import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { loadNearbyExplore, localExploreRefreshKey } from '../data/nearby-explore-api';
import type { JourneyMapPosition } from './codex-map';
import type { ExploreViewport } from './local-map-bounds';

/** One current viewport; panning never accumulates a location history or an entire catalog. */
export function useViewportExplore(position: JourneyMapPosition | undefined, enabled: boolean) {
  const [bbox, setBbox] = useState<ExploreViewport>();
  const positionRef = useRef(position);
  const enabledRef = useRef(enabled);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastRequest = useRef(0);
  useEffect(() => {
    positionRef.current = position;
  }, [position]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) clearTimeout(timer.current);
  }, [enabled]);
  const onViewport = useCallback((next: ExploreViewport) => {
    if (!enabledRef.current) return;
    clearTimeout(timer.current);
    // MapLibre already debounces moveend; this bounds repeated deliberate pans to 20/minute.
    timer.current = setTimeout(
      () => {
        lastRequest.current = Date.now();
        setBbox((current) =>
          current?.every((value, index) => value === next[index]) ? current : next,
        );
      },
      Math.max(0, 3_000 - (Date.now() - lastRequest.current)),
    );
  }, []);
  const positionKey = position ? localExploreRefreshKey(position) : undefined;
  const query = useQuery({
    queryKey: ['explore', 'viewport', bbox, positionKey, enabled],
    queryFn: ({ signal }) => loadNearbyExplore(positionRef.current!, { bbox, signal }),
    enabled: enabled && Boolean(position && bbox),
    gcTime: 0,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return { onViewport, query };
}
